import React, { useEffect, useState, useMemo } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Switch, Popconfirm, message, Space, Tag, Card, Row, Col, Statistic, Collapse, Select, Grid } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, FilterOutlined, ClearOutlined } from '@ant-design/icons';
import { kasalarAPI } from '../services/api';
import { formatPara, paraInputProps } from '../utils/format';

function Kasalar() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [aramaMetni, setAramaMetni] = useState('');
  const [filtreDurum, setFiltreDurum] = useState([]);
  const [filtreBakiyeOp, setFiltreBakiyeOp] = useState(null);
  const [filtreBakiyeDeger, setFiltreBakiyeDeger] = useState(null);
  const [filtreAcik, setFiltreAcik] = useState([]);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await kasalarAPI.getAll();
      setData(res.data);
    } catch (err) {
      message.error('Kasalar yüklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (values) => {
    try {
      if (editingRecord) {
        const payload = { ad: values.ad, aciklama: values.aciklama, aktif: values.aktif ? 1 : 0 };
        await kasalarAPI.update(editingRecord.id, payload);
        message.success('Kasa güncellendi');
      } else {
        const payload = { ad: values.ad, acilis_bakiyesi: values.acilis_bakiyesi || 0, aciklama: values.aciklama, aktif: values.aktif ? 1 : 0 };
        await kasalarAPI.create(payload);
        message.success('Kasa eklendi');
      }
      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
      fetchData();
    } catch (err) {
      message.error((editingRecord ? 'Güncellenemedi: ' : 'Eklenemedi: ') + (err.response?.data?.hata || err.message));
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ad: record.ad,
      aciklama: record.aciklama,
      aktif: !!record.aktif,
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await kasalarAPI.remove(id);
      message.success('Kasa silindi');
      fetchData();
    } catch (err) {
      message.error('Silinemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const filteredData = useMemo(() => {
    return data.filter(d => {
      if (aramaMetni && !(d.ad || '').toLowerCase().includes(aramaMetni.toLowerCase())) return false;
      if (filtreDurum.length > 0 && !filtreDurum.includes(d.aktif ? 1 : 0)) return false;
      if (filtreBakiyeOp && filtreBakiyeDeger != null) {
        const bakiye = Number(d.bakiye || 0), deger = Number(filtreBakiyeDeger);
        if (filtreBakiyeOp === 'gt' && !(bakiye > deger)) return false;
        if (filtreBakiyeOp === 'gte' && !(bakiye >= deger)) return false;
        if (filtreBakiyeOp === 'lt' && !(bakiye < deger)) return false;
        if (filtreBakiyeOp === 'lte' && !(bakiye <= deger)) return false;
        if (filtreBakiyeOp === 'eq' && !(bakiye === deger)) return false;
        if (filtreBakiyeOp === 'ne' && !(bakiye !== deger)) return false;
      }
      return true;
    });
  }, [data, aramaMetni, filtreDurum, filtreBakiyeOp, filtreBakiyeDeger]);

  const toplamlar = useMemo(() => {
    const t = { adet: filteredData.length, bakiye: 0, tahsilat: 0, odeme: 0, virmanGiris: 0, virmanCikis: 0 };
    filteredData.forEach(d => {
      t.bakiye += Number(d.bakiye || 0);
      t.tahsilat += Number(d.toplam_tahsilat || 0);
      t.odeme += Number(d.toplam_odeme || 0);
      t.virmanGiris += Number(d.toplam_giris_virman || 0);
      t.virmanCikis += Number(d.toplam_cikis_virman || 0);
    });
    return t;
  }, [filteredData]);

  const filtreAktifMi = filtreDurum.length > 0 || filtreBakiyeOp || !!aramaMetni;
  const filtreTemizle = () => { setFiltreDurum([]); setFiltreBakiyeOp(null); setFiltreBakiyeDeger(null); };

  const columns = [
    { title: 'Kasa Adı', dataIndex: 'ad', key: 'ad', sorter: (a, b) => (a.ad || '').localeCompare(b.ad || '', 'tr') },
    { title: 'Toplam Tahsilat', dataIndex: 'toplam_tahsilat', key: 'toplam_tahsilat', width: 150, align: 'right', responsive: ['md'], render: (v) => <span style={{ color: '#389e0d' }}>{formatPara(v || 0)}</span>, sorter: (a, b) => (a.toplam_tahsilat || 0) - (b.toplam_tahsilat || 0) },
    { title: 'Toplam Ödeme', dataIndex: 'toplam_odeme', key: 'toplam_odeme', width: 150, align: 'right', responsive: ['md'], render: (v) => <span style={{ color: '#cf1322' }}>{formatPara(v || 0)}</span>, sorter: (a, b) => (a.toplam_odeme || 0) - (b.toplam_odeme || 0) },
    { title: 'Giriş Virman', dataIndex: 'toplam_giris_virman', key: 'toplam_giris_virman', width: 130, align: 'right', responsive: ['lg'], render: (v) => <span style={{ color: '#1677ff' }}>{formatPara(v || 0)}</span>, sorter: (a, b) => (a.toplam_giris_virman || 0) - (b.toplam_giris_virman || 0) },
    { title: 'Çıkış Virman', dataIndex: 'toplam_cikis_virman', key: 'toplam_cikis_virman', width: 130, align: 'right', responsive: ['lg'], render: (v) => <span style={{ color: '#d48806' }}>{formatPara(v || 0)}</span>, sorter: (a, b) => (a.toplam_cikis_virman || 0) - (b.toplam_cikis_virman || 0) },
    { title: 'Bakiye', dataIndex: 'bakiye', key: 'bakiye', width: isMobile ? 100 : 140, align: 'right', render: (v) => <strong>{formatPara(v || 0)}</strong>, sorter: (a, b) => (a.bakiye || 0) - (b.bakiye || 0) },
    { title: 'Durum', dataIndex: 'aktif', key: 'aktif', width: 90, responsive: ['sm'], render: (v) => v ? <Tag color="green">Aktif</Tag> : <Tag color="red">Pasif</Tag> },
    { title: 'Açıklama', dataIndex: 'aciklama', key: 'aciklama', responsive: ['lg'] },
    {
      title: 'İşlem', key: 'islem', width: isMobile ? 80 : 160,
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>{!isMobile && 'Düzenle'}</Button>
          <Popconfirm title="Bu kasayı silmek istediğinize emin misiniz?" onConfirm={() => handleDelete(record.id)} okText="Evet" cancelText="Hayır">
            <Button danger size="small" icon={<DeleteOutlined />}>{!isMobile && 'Sil'}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 24, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>Kasalar</h2>
        <Space size={6} style={{ flexShrink: 0 }}>
          <Input placeholder="Ara..." prefix={<SearchOutlined />} allowClear onChange={(e) => setAramaMetni(e.target.value)} style={{ width: isMobile ? 110 : 200 }} />
          <Button icon={<FilterOutlined />} onClick={() => setFiltreAcik(filtreAcik.length ? [] : ['1'])} type={filtreAktifMi ? 'primary' : 'default'} ghost={filtreAktifMi}
            title={filtreAktifMi ? 'Filtreler (Aktif)' : 'Filtreler'}>
            {!isMobile && `Filtreler${filtreAktifMi ? ' (Aktif)' : ''}`}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)} title="Yeni Kasa">
            {!isMobile && 'Yeni Kasa'}
          </Button>
        </Space>
      </div>

      <Collapse activeKey={filtreAcik} onChange={setFiltreAcik} ghost items={[{ key: '1', showArrow: false, header: null, children: (
        <Card size="small" style={{ marginBottom: 16 }} title={<span><FilterOutlined /> Akıllı Filtreler</span>} extra={filtreAktifMi && <Button size="small" icon={<ClearOutlined />} onClick={filtreTemizle}>Temizle</Button>}>
          <Row gutter={[16, 12]}>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Durum</label>
              <Select mode="multiple" style={{ width: '100%' }} placeholder="Durum seçin..." options={[{ value: 1, label: 'Aktif' }, { value: 0, label: 'Pasif' }]} value={filtreDurum} onChange={setFiltreDurum} allowClear />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <label style={{ fontSize: 12, color: '#666', display: 'block', marginBottom: 4 }}>Bakiye Filtresi</label>
              <Space.Compact style={{ width: '100%' }}>
                <Select style={{ width: '45%' }} placeholder="Koşul" value={filtreBakiyeOp} onChange={setFiltreBakiyeOp} allowClear options={[{ value: 'gt', label: '>' }, { value: 'gte', label: '>=' }, { value: 'lt', label: '<' }, { value: 'lte', label: '<=' }, { value: 'eq', label: '=' }, { value: 'ne', label: '≠' }]} />
                <InputNumber {...paraInputProps} style={{ width: '55%' }} placeholder="Tutar" value={filtreBakiyeDeger} onChange={setFiltreBakiyeDeger} />
              </Space.Compact>
            </Col>
          </Row>
        </Card>
      )}]} />

      <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }} title={filtreAktifMi ? 'Filtrelenen Toplamlar' : undefined}>
        <Row gutter={16}>
          <Col xs={12} sm={8} md={4}><Statistic title="Kasa Sayısı" value={toplamlar.adet} /></Col>
          <Col xs={12} sm={8} md={4}><Statistic title="Toplam Tahsilat" value={toplamlar.tahsilat} precision={2} suffix="₺" valueStyle={{ color: '#3f8600' }} /></Col>
          <Col xs={12} sm={8} md={4}><Statistic title="Toplam Ödeme" value={toplamlar.odeme} precision={2} suffix="₺" valueStyle={{ color: '#cf1322' }} /></Col>
          <Col xs={12} sm={8} md={4}><Statistic title="Giriş Virman" value={toplamlar.virmanGiris} precision={2} suffix="₺" valueStyle={{ color: '#1677ff' }} /></Col>
          <Col xs={12} sm={8} md={4}><Statistic title="Çıkış Virman" value={toplamlar.virmanCikis} precision={2} suffix="₺" valueStyle={{ color: '#d48806' }} /></Col>
          <Col xs={12} sm={8} md={4}><Statistic title="Toplam Bakiye" value={toplamlar.bakiye} precision={2} suffix="₺" /></Col>
        </Row>
      </Card>

      <Table columns={columns} dataSource={filteredData} rowKey="id" loading={loading} pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '20', '50'], showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt` }} />

      <Modal title={editingRecord ? 'Kasa Düzenle' : 'Yeni Kasa'} open={modalOpen} onCancel={() => { setModalOpen(false); setEditingRecord(null); form.resetFields(); }} onOk={() => form.submit()} width={420}>
        <Form form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ aktif: true }}>
          <Form.Item name="ad" label="Kasa Adı" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          {!editingRecord && (
            <Form.Item name="acilis_bakiyesi" label="Açılış Bakiyesi (₺)" rules={[{ required: true, message: 'Açılış bakiyesi zorunludur' }]}>
              <InputNumber {...paraInputProps} min={0} style={{ width: '100%' }} />
            </Form.Item>
          )}
          <Form.Item name="aktif" label="Durum" valuePropName="checked">
            <Switch checkedChildren="Aktif" unCheckedChildren="Pasif" />
          </Form.Item>
          <Form.Item name="aciklama" label="Açıklama">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Kasalar;
